
















































































import { Component, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";
import SchoolMixin from "@/mixins/school.mixin";
import moment from "moment";
import CryptoJS from "crypto-js";

interface MonitoringVisit {
  monitoringDate?: string;
  inspectionDate?: string;
  monitoringType?: string;
  inspectionType?: string;
  hasViolations?: boolean | string | null;
  violationsCited?: boolean | string | null;
  statementOfViolations?: string | null;
  documentType?: string | null;
  dataSource?: string | null;
  docId?: string | null;
  docPopUrl?: string | null;
}

@Component({
  computed: { ...mapState("app", ["school"]) }
})
export default class SchoolBasedMonitoring extends Mixins(SchoolMixin) {
  public visits: MonitoringVisit[] = [];
  public reportError = false;
  private sortBy = "monitoringDate";
  private sortDesc = true;
  private PDFTimeout = "600000";

  async created() {
    await this.loadMonitoringVisits();
  }

  async loadMonitoringVisits() {
    try {
      const result = await this.$store.dispatch(
        "app/getMonitoringVisits",
        this.schoolAbout.id
      );
      if (!Array.isArray(result)) {
        throw new Error("Monitoring visits response was not a list.");
      }
      this.visits = result as MonitoringVisit[];
      const timeout = await this.$store.dispatch("app/getPDFURLTimeout");
      if (timeout) {
        this.PDFTimeout = String(timeout);
      }
    } catch (error) {
      console.error("Unable to load school-based monitoring visits.", error);
    }
  }

  get monitoringVisits() {
    return this.visits
      .map(visit => ({
        ...visit,
        monitoringDate: visit.monitoringDate || visit.inspectionDate,
        monitoringType: visit.monitoringType || visit.inspectionType,
        hasViolations: this.hasViolations(visit)
      }));
  }

  private hasViolations(visit: MonitoringVisit): boolean {
    const explicitValue =
      visit.hasViolations ??
      visit.violationsCited ??
      visit.statementOfViolations;

    if (explicitValue !== undefined && explicitValue !== null) {
      if (typeof explicitValue === "boolean") {
        return explicitValue;
      }
      const normalizedValue = String(explicitValue)
        .trim()
        .toLowerCase();
      if (["y", "yes", "true", "1"].includes(normalizedValue)) {
        return true;
      }
      if (["n", "no", "false", "0", ""].includes(normalizedValue)) {
        return false;
      }
      return !normalizedValue.includes("no violation");
    }

    const documentType = String(visit.documentType || "").toLowerCase();
    return !(
      documentType.includes("license specialist") &&
      ["l", "e"].includes(String(visit.dataSource || "").toLowerCase())
    );
  }

  private monitoringFields = [
    {
      key: "monitoringDate",
      label: "Monitoring Date",
      sortable: true,
      class: "text-left align-middle"
    },
    {
      key: "monitoringType",
      label: "Monitoring Type",
      sortable: false,
      class: "text-left align-middle"
    },
    {
      key: "statementOfViolations",
      label: "Statement of Violations",
      sortable: false,
      class: "text-left align-middle"
    }
  ];

  formatDate(date: Date | string) {
    return date ? moment(date).format("MM-DD-YYYY") : "";
  }

  async onShowPdf(item: MonitoringVisit) {
    this.reportError = false;
    try {
      if (!item.docId) {
        throw new Error("Monitoring visit does not have a document ID.");
      }

      const isEdlinkViewer = await this.$store.dispatch(
        "app/getIsEdlinkViewer"
      );
      if (isEdlinkViewer === "Y") {
        const expirationTimestamp = Date.now() + parseInt(this.PDFTimeout, 10);
        const encryptedParams = this.encrypt(
          JSON.stringify({ id: item.docId, t: expirationTimestamp })
        );
        const route = this.$router.resolve({
          path: `/Inspections-report/${encodeURIComponent(encryptedParams)}`
        });
        window.open(route.href.replaceAll("%2B", "Edlink"), "_blank");
        return;
      }

      const result = await this.$store.dispatch("app/viewPdfData", item.docId);
      if (!result || !result.docPopURL) {
        throw new Error("Monitoring report URL was not returned.");
      }
      window.open(result.docPopURL, "_blank");
    } catch {
      this.reportError = true;
    }
  }

  private encrypt(message: string) {
    return CryptoJS.AES.encrypt(message, "SF").toString();
  }
}
